import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import config from 'modules/Referral/PayoutProfiles/Invoices/filters'
import { DatedList } from 'gu-ui-kit'
import EmptyResults from 'components/EmptyResults'
import styles from 'modules/Referral/PayoutProfiles/Invoices/invoices.module.scss'
import formatRelative from 'utils/dateFns/formatRelative'
import { useTranslation } from 'react-i18next'
import Item from './subcomponents/Row'
import FilterPanel from 'components/Filters/FilterPanel'
import { useParams } from 'react-router'
import PageHeader from 'components/PageHeader'
import Breadcrumbs from 'components/Breadcrumbs'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'
import { ReactComponent as EmptyResultsSVG } from 'images/illustration/empty-list.svg'
import clsx from 'clsx'
import GridSpinner from 'components/UI/GridSpinner'
import useTotallyEmptyList from 'utils/useTotallyEmptyList'

const Invoices = () => {
  const { profile_id } = useParams()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const pathGen = useGeneratePathI18n()

  useEffect(() => {
    dispatch({
      type: 'REFERRAL/PAYOUT_PROFILES_FETCH',
      payload: {
        params: {
          offset: (page - 1) * pagination.limit,
          limit: 10000,
        },
      },
    })
  }, [])

  const { profiles, data, pagination, loaded, loading, loadFailed } = useSelector(
    (state) => ({
      profiles: state['referral/payout-profiles'].data,
      data: state['referral/payouts-list'].data,
      pagination: state['referral/payouts-list'].pagination,
      loaded: state['referral/payouts-list'].loaded,
      loading: state['referral/payouts-list'].loading,
      loadFailed: state['referral/payouts-list'].loadFailed,
    }),
    shallowEqual,
  )

  const [page, setPage] = useState(1)

  const profile = useMemo(() => profiles.find((i) => i.id === +profile_id), [profiles, profile_id])

  const filterManager = useFiltersManager({
    filters: useSelector((state) => state['referral/payouts-list'].filters),
    config: config(),
    setAction: 'REFERRAL/PAYOUTS_LIST_SET_FILTER',
    additionalFilterState: { profile_id },
    localStorageKey: 'referral/payouts-list',
  })

  useEffect(() => {
    filterManager.updateFilterConfigurationByKey({
      key: 'profile_id',
      val: { options: profiles.map((i) => '' + i.id) },
    })
  }, [profiles])

  useEffect(() => {
    dispatch({
      type: 'REFERRAL/PAYOUTS_LIST_DROP_DATA',
    })
    setPage(1)
  }, [filterManager.params])

  useEffect(() => {
    filterManager.params &&
      dispatch({
        type: 'REFERRAL/PAYOUTS_LIST_FETCH',
        payload: {
          ...filterManager.params,
          profile_id,
          offset: pagination.limit * (page - 1),
          limit: pagination.limit,
        },
      })
  }, [filterManager.params, page])

  const invoices = useMemo(() => data.map((item) => ({ ...item, date: item.created_at })), [data])

  const headerText = useMemo(() => {
    if (!profile) {
      return
    }

    const { type, card_number, wallet, name } = profile

    return (
      <>
        {t(`common::payout_types.${type}`) + ': '}
        <span className={'num'}>{type === 'bank_card' ? card_number : wallet || name}</span>
      </>
    )
  }, [profile])

  const breadcrumbsConfig = useMemo(
    () => [
      {
        title: t('referral::navigation.payout-profiles'),
        to: pathGen(ROUTES.REFERRAL_PAYOUT_PROFILES),
      },
    ],
    [headerText, t],
  )

  const [totallyEmptyList, firstLoad] = useTotallyEmptyList(filterManager.params, data, loaded)

  return (
    <>
      <Breadcrumbs links={breadcrumbsConfig} />
      <PageHeader className={clsx('margin-bottom-40', styles['page-header'])}>
        {headerText}
      </PageHeader>

      {firstLoad && <GridSpinner width={32} />}

      {!firstLoad && totallyEmptyList && (
        <EmptyResults
          header={t('referral::payouts_invoices.empty_list.header')}
          text={t('referral::payouts_invoices.empty_list.text')}
          image={<EmptyResultsSVG />}
        />
      )}

      {!firstLoad && !totallyEmptyList && (
        <>
          <FilterPanel
            filterManager={filterManager}
            className={'margin-bottom-32'}
          />
          <DatedList
            emptyComponent={
              <EmptyResults
                header={t('referral::payouts_invoices.data_list.empty.header')}
                text={t('referral::payouts_invoices.data_list.empty.text')}
                image={<EmptyResultsSVG />}
              />
            }
            itemComponent={Item}
            pagination={pagination}
            items={invoices}
            moreButtonLabel={t('referral::payouts_invoices.showMore')}
            handleGetMoreItems={() => setPage(page + 1)}
            loading={loading}
            loaded={loaded}
            loadFailed={loadFailed}
            l10n={{
              formatFunction: formatRelative,
              today: t('common::dated_list.date_format.today'),
              yesterday: t('common::dated_list.date_format.yesterday'),
              formatDates: {
                short: t('common::dated_list.date_format.short'),
                long: t('common::dated_list.date_format.long'),
              },
            }}
          />
        </>
      )}
    </>
  )
}

export default Invoices
